import { LETTRES } from '../../utils/qcmTheme';

/**
 * Règles de l'écran projeté en fin de séance — podium et revue de correction.
 *
 * Extraites de `ProjectionQCM.jsx` pour être testables sans DOM : ce sont
 * elles qui décident QUEL écran est projeté et CE QU'IL montre, à partir de
 * deux entrées seulement — l'étape de la séance et la charge de
 * `GET /sessions-qcm/{id}/resultats/`.
 *
 * Principe unique : L'ÉTAPE FAIT FOI, plus le statut. Depuis la décision D3 du
 * chantier, la séance reste `en_cours` pendant le podium et la revue pour que
 * le flux SSE — et donc le pilotage depuis le téléphone — survive jusqu'au
 * bout. Brancher sur `statut === 'terminee'` comme avant n'atteindrait plus
 * jamais le podium.
 */

/**
 * Quel écran la projection doit rendre.
 *
 * `'quiz'` est le corps de séance historique (attente, question, reveal) ;
 * `'podium'`, `'revue'` et `'fin'` sont les trois écrans de fin.
 *
 * Le cas `statut === 'terminee'` avec `etape === 'quiz'` n'est pas un état que
 * ce chantier produit — `end/` comme `etape/` posent tous deux `etape='fin'`.
 * C'est l'état des séances closes AVANT la migration 0067, dont le défaut de
 * colonne a rempli `etape` à `'quiz'` : elles montraient le podium, elles
 * continuent.
 */
export const ecranProjection = ({ statut, etape }) => {
  if (etape === 'podium') return 'podium';
  if (etape === 'revue') return 'revue';
  if (statut === 'terminee') return etape === 'quiz' ? 'podium' : 'fin';
  return 'quiz';
};

/**
 * La phase de l'écran de question, avant les écrans de fin (lot « levez vos
 * cartes », 2026-09-19).
 *
 * `'reflexion'` : l'énoncé et les choix, personne n'est nommé, personne ne
 * lève. `'scan'` : l'énoncé disparaît, la consigne et les prénoms en grand.
 * `'reveal'` : la correction, l'énoncé revient. `'attente'` : l'écran d'avant
 * le lot (énoncé + bande des prénoms dès la question), gardé tant que le
 * serveur ne sait pas ouvrir le scan, c'est-à-dire tant que la séance n'a pas
 * la clé `scan_ouvert_a`. Aucune régression possible entre deux déploiements.
 *
 * Le scan est ouvert par le serveur (événement `scan_ouvert`, posé par le
 * bouton Scan ! du téléphone), ET par une première réponse reçue : si l'appel
 * du téléphone s'est perdu, les cartes sont pourtant lues, et la classe doit
 * voir les prénoms s'envoler. Un appel perdu ne bloque jamais une classe.
 */
export const phaseQuestion = ({
  revealed = false, scanOuvert = false, nbReponses = 0, backSaitOuvrir = true,
} = {}) => {
  if (revealed) return 'reveal';
  if (!backSaitOuvrir) return 'attente';
  if (scanOuvert || nbReponses > 0) return 'scan';
  return 'reflexion';
};

/**
 * Le flux doit-il rester ouvert ?
 *
 * Une séance `terminee` en revue garde son flux (le serveur ne lui envoie plus
 * `session_terminee`, phase 1) : sans ça, la reprise de correction depuis le
 * tableau de bord (D11) n'aurait aucun canal pour suivre les « précédent /
 * suivant » du téléphone.
 *
 * Partagée par la projection ET par le téléphone (`SessionLive`, phase 4) :
 * les deux écrans vivent sur le même flux, une seconde copie de la règle
 * divergerait au premier ajustement.
 */
export const fluxOuvert = ({ statut, etape }) => statut !== 'terminee' || etape === 'revue';

/**
 * Les résultats sont-ils nécessaires ? Un seul chargement, à l'entrée dans le
 * premier écran de fin ; la navigation de revue se fait ensuite sur les
 * données déjà en main, sans refetch par `revue_ordre`.
 */
export const resultatsNecessaires = ({ statut, etape }) => ecranProjection({ statut, etape }) !== 'quiz';

/**
 * Classement du podium — jusqu'à trois rangs, ex æquo groupés.
 *
 * Déplacé tel quel depuis `ProjectionQCM.jsx` : même calcul, mais testable.
 * Un rang est un GROUPE d'entrées à score égal ; la boucle s'arrête au
 * quatrième rang, pas à la troisième entrée.
 */
export const calculerPodiumRanks = (resultats, mode) => {
  if (!resultats?.resultats) return [];
  const map = new Map();
  const isEquipe = mode === 'equipe';
  resultats.resultats.forEach((q) => {
    (q.reponses || []).forEach((r) => {
      const nom = isEquipe ? r.equipe_nom : r.eleve_nom;
      if (!nom) return;
      if (!map.has(nom)) map.set(nom, { correct: 0, total: 0 });
      const s = map.get(nom);
      s.total++;
      if (r.est_correcte) s.correct++;
    });
  });
  const sorted = [...map.entries()]
    .map(([nom, s]) => ({ nom, score: s.correct, total: s.total }))
    .sort((a, b) => b.score - a.score || a.nom.localeCompare(b.nom));
  if (sorted.length === 0) return [];
  const groups = [];
  let currentRank = 1;
  let currentScore = sorted[0].score;
  let currentGroup = { rank: 1, entries: [sorted[0]] };
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].score === currentScore) {
      currentGroup.entries.push(sorted[i]);
    } else {
      groups.push(currentGroup);
      currentRank = groups.length + 1;
      if (currentRank > 3) break;
      currentScore = sorted[i].score;
      currentGroup = { rank: currentRank, entries: [sorted[i]] };
    }
  }
  if (currentRank <= 3) groups.push(currentGroup);
  return groups;
};

/**
 * La lettre A-D à mettre en vedette, ou `null` si on ne sait pas laquelle.
 *
 * Sur une session cartes, `contenu.bonne_reponse` EST une lettre : c'est à elle
 * que le modèle compare le scan pour décider `est_correcte`
 * (`ReponseQCM.save()`). On garde le repli par le texte du choix pour les
 * questions dont la banque stocke l'énoncé de la réponse — mieux vaut ne rien
 * mettre en vedette que la mauvaise tuile.
 */
export const lettreBonneReponse = (choix, bonneReponse) => {
  if (!bonneReponse) return null;
  const brut = String(bonneReponse).trim().toUpperCase();
  if (LETTRES.includes(brut)) return brut;
  const i = (choix || []).findIndex(
    (c) => typeof c === 'string' && c.trim().toUpperCase() === brut,
  );
  return i >= 0 ? LETTRES[i] : null;
};

/**
 * Couleur et message d'une question selon sa réussite — les seuils historiques
 * du reveal. Partagés par le reveal en cours de quizz et par la carte de revue :
 * deux échelles pour la même donnée divergeraient au premier ajustement.
 */
export const paletteReussite = (pctCorrect) => {
  if (pctCorrect >= 70) return { couleur: '#34d399', message: 'Bravo !' };
  if (pctCorrect >= 50) return { couleur: '#fbbf24', message: 'Pas mal, on y est presque !' };
  if (pctCorrect >= 20) return { couleur: '#fb923c', message: 'Courage, on progresse !' };
  return { couleur: '#fb7185', message: 'On va travailler ensemble !' };
};

/**
 * La carte projetée pour un `revue_ordre` donné : énoncé, bonne réponse et
 * pourcentage de la classe qui a trouvé juste (D5). `null` quand l'ordre ne
 * désigne aucune question — l'écran retombe alors sur son état d'attente
 * plutôt que de rendre du vide.
 *
 * Le pourcentage se calcule sur les réponses REÇUES, pas sur l'effectif : c'est
 * la même base que le reveal par question, sans quoi une question posée à une
 * classe incomplète paraîtrait ratée.
 */
export const carteRevue = (resultats, revueOrdre) => {
  const q = (resultats?.resultats || []).find((r) => r.question_ordre === revueOrdre);
  if (!q) return null;
  const reponses = q.reponses || [];
  const nbBonnes = reponses.filter((r) => r.est_correcte).length;
  const pctCorrect = reponses.length === 0 ? 0 : Math.round((nbBonnes / reponses.length) * 100);
  return {
    questionOrdre: q.question_ordre,
    texte: q.texte,
    choix: q.choix || [],
    elements: q.elements_additionnels?.elements,
    bonneReponse: q.bonne_reponse || '',
    bonneLettre: lettreBonneReponse(q.choix, q.bonne_reponse),
    nbReponses: reponses.length,
    nbBonnes,
    pctCorrect,
    couleur: paletteReussite(pctCorrect).couleur,
  };
};
